import { useCallback, useContext, useRef } from 'react'
import ModalContext from 'contexts/ModalContext'
import useLoyaltyChooseDoubleRewards from './useLoyaltyChooseDoubleRewards'
import LoyaltyChooseDoubleRewardsFlowModal from '../components/LoyaltyChooseDoubleRewardsFlowModal'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import useLoyaltyChooseDoubleRewardsRegion from './useLoyaltyChooseDoubleRewardsRegion'
import useLoyaltyChooseDoubleRewardsTracking from './useLoyaltyChooseDoubleRewardsTracking'

/**
 * Opens login, membership enrolment, or reward selection as appropriate.
 * Ignores repeated clicks while a modal is open and campaigns without an end date.
 */
function useLoyaltyOpenChooseDoubleRewardsFlow() {
  const { track } = useLoyaltyChooseDoubleRewardsTracking('choice_modal')
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const loggedIn = useAppSelector(selectLoggedIn)
  const account = useAppSelector(state => state.loyalty.account)
  const { endsAt, canEnrol } = useLoyaltyChooseDoubleRewards()
  const { needsRegionSwitch, switchToLoyaltyRegion } = useLoyaltyChooseDoubleRewardsRegion()
  const openRef = useRef(false)

  const joinLabel = intl.formatMessage({
    id: 'h6PuS4suQF1JuReZxhxN',
    defaultMessage: "Join {programName} now - it's free",
    description: 'Join membership action shared by all sections of the double rewards landing page',
  }, { programName: getLoyaltyProgramDisplayName() })

  return useCallback(async() => {
    if (needsRegionSwitch) {
      switchToLoyaltyRegion()
      return
    }
    if (!endsAt || openRef.current) return
    openRef.current = true
    try {
      if (!loggedIn) {
        track('sign_in_clicked')
        const result = await showModal<AccountAccessModalResult>(<AccountAccessModal skipLoyaltyAutoEnrolment />)
        if (result?.loggedIn) {
          dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
        }
        return
      }
      if (account.initial || account.fetching || account.error) return
      if (!account.data) {
        track('join_clicked')
        const confirmed = await showModal<boolean>(<LoyaltyJoinModal onAfterClose={undefined} primaryActionCta={joinLabel} />)
        if (confirmed) dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
        return
      }
      if (!canEnrol) return
      await showModal(<LoyaltyChooseDoubleRewardsFlowModal />)
    } finally {
      openRef.current = false
    }
  }, [endsAt, showModal, loggedIn, account, dispatch, joinLabel, canEnrol, needsRegionSwitch, switchToLoyaltyRegion, track])
}

export default useLoyaltyOpenChooseDoubleRewardsFlow
